// ABOUTME: Shared filter utilities for admin components (child filtering, dropdown options)
// ABOUTME: Provides reusable filtering logic for AdminFamilyDetail, AdminChildDetail, and future admin components

/**
 * Minimal child interface for dropdown filtering
 * Supports both API Child interface (name) and local FamilyChild interface (firstName)
 */
interface ChildForDropdown {
  id: number;
  firstName?: string;
  name?: string;
}

/**
 * Child dropdown option for filtering by child in admin components
 */
export interface ChildDropdownOption {
  value: number | null;
  label: string;
}

/**
 * Generate child dropdown options for admin component filters
 * Includes "All Children" option (null value) as first option
 * Works with both firstName (FamilyChild) and name (Child) interfaces
 *
 * @param children - Array of child objects with id and firstName or name
 * @returns Array of ChildDropdownOption with "All Children" first, followed by each child
 *
 * @example
 * ```tsx
 * const children = [
 *   { id: 1, firstName: 'Alice', ... },
 *   { id: 2, firstName: 'Bob', ... }
 * ];
 *
 * const options = getChildDropdownOptions(children);
 * // Returns: [
 * //   { value: null, label: 'All Children' },
 * //   { value: 1, label: 'Alice' },
 * //   { value: 2, label: 'Bob' }
 * // ]
 * ```
 */
export const getChildDropdownOptions = (children: ChildForDropdown[]): ChildDropdownOption[] => {
  const options: ChildDropdownOption[] = [{ value: null, label: 'All Children' }];
  children.forEach(child => {
    // Support both firstName (FamilyChild) and name (Child) fields
    const displayName = child.firstName || child.name || 'Unknown';
    options.push({ value: child.id, label: displayName });
  });
  return options;
};
